import {useShoppingCart} from "../../../hooks/useShoppingCart";
import styled from "styled-components";
import {ICartItem} from "../../../interfaces/ICartItem";
import MiniCartProduct from "../../../components/product/MiniCartProduct";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import TitleArea from "../../../components/TitleArea";
import Button from "../../../components/Button";
import {useMessages} from "../../../hooks/useMessages";
import {useRouter} from "next/router";
import Image from "next/legacy/image";
import React, {useContext, useEffect, useState} from "react";
import {LoggedOutCartContext} from "../../../contexts/LoggedOutCartContext";
import {ILoggedOutShoppingList} from "../../../interfaces/ILoggedOutShoppingList";
import {useUserLoggedIn} from "../../../hooks/useUserLoggedIn";
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useCountryLang} from "../../../hooks/useCountryLang";
import {AddItemType} from "../../../types/AddItemType";
import {ITotalForProducts} from "../../../interfaces/ITotalForProducts";
import {request} from "../../../shared/httpInterceptor";
import {ShowLoginContext} from "../../../contexts/ShowLoginContext";
import PageHead from "../../../components/PageHead";
import {Spinner} from "react-bootstrap";
import {getLocalStorageItem, removeLocalStorageItem} from "../../../shared/localStorageUtil";
import {getTheme} from "../../../shared/themeUtils";
import {useShippingCosts} from "../../../hooks/useShippingCosts";
import {withServerSideProps} from "../../../shared/withServerSideProps";
import RecentlyViewedProducts from "../../../components/product/RecentlyViewedProducts";

const CartList = styled.div`
`

const CheckoutArea = styled.div`
  position: sticky;
  top: 96px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: ${getTheme("colors.background")};
  padding: ${getTheme("space.3")};
  margin: ${getTheme("space.2")} 0;
  border-radius: ${getTheme("borderRadius")};
  text-align: center;

    .shipping-costs {
        margin-bottom: ${getTheme("space.1")};
        position: relative;
        bottom: ${getTheme("space.0")};
        font-size: ${getTheme("fontSizes.small")};
        font-weight: 500;
    }

  .box-content {
    margin-bottom: ${getTheme("space.3")};
  }

  .total {
    font-weight: bold;
    font-size: ${getTheme("fontSizes.xLarge")};
    margin-bottom: ${getTheme("space.0")};
  }

  .discounts-message {
    font-size: ${getTheme("fontSizes.xSmall")};
    color: ${getTheme("colors.secondaryFont")};
  }
`

const SecondarySection = styled.div`
  margin: ${getTheme("space.3")} 0;
`

const EmptyCart = styled.div`
  height: 100%;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${getTheme("colors.secondaryFont")};
  margin-bottom: 60px;
`

export const fetchLoggedOutCartTotal = async (loggedOutCartCtx: ILoggedOutShoppingList[] | null,
                                              countryLang: string | null) => {
  const products: AddItemType[] = [];
  if (loggedOutCartCtx && loggedOutCartCtx.length > 0) {
    loggedOutCartCtx.map(entry => {
      products.push({
        productId: entry.product.productID,
        quantity: entry.quantity
      })
    })
  }

  // also include the setupOptions in the loggedOut cart total calculation
  const loggedOutSetupOptions = getLocalStorageItem("loggedOutSetupOptions")
  if (loggedOutSetupOptions) {
    const setupEntries = [...JSON.parse(loggedOutSetupOptions)]
    setupEntries.forEach(entry => products.push(entry))
  }

  const res = await request({url: `/${countryLang}/totalForProducts`, data: products, method: 'post'})
  return res.data;
}


const Cart = () => {
  const router = useRouter()
  const countryLang = useCountryLang()
  const shoppingCartQuery = useShoppingCart()
  const messagesQuery = useMessages()
  const isUserLoggedIn = useUserLoggedIn()
  const [hasItems, setHasItems] = useState(false)
  const {loggedOutCartCtx, setLoggedOutCartCtx} = useContext(LoggedOutCartContext)
  const {setShowLoginContext} = useContext(ShowLoginContext);
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)
  const shippingCostsQuery = useShippingCosts()

  useEffect(() => {
    setHasItems(false)
    if (shoppingCartQuery.data) {
      setHasItems(shoppingCartQuery.data.products.length > 0)
    }
    if (loggedOutCartCtx && loggedOutCartCtx.length > 0) {
      setHasItems(true)
    }
  }, [shoppingCartQuery.data, loggedOutCartCtx])


  const loggedOutCartTotalQuery = useQuery<ITotalForProducts>(["loggedOutCartTotal", loggedOutCartCtx?.map(entry => entry.quantity)],
    () => fetchLoggedOutCartTotal(loggedOutCartCtx, countryLang), {
    enabled: countryLang != null && (loggedOutCartCtx != null && loggedOutCartCtx.length > 0)
  })


  const handleToCheckout = async () => {
    await router.push(`/${countryLang}/checkout`)
  }


  const handleLogin = () => {
    setShowLoginContext(true)
  }


  const handleClearCart = async () => {
    setIsLoading(true)
    await request({url: `/${countryLang}/deleteCart`, method: 'DELETE'})
    await queryClient.invalidateQueries(["shopping-cart", countryLang]);
    await queryClient.invalidateQueries(["checkout-shopping-cart", countryLang]);
    setIsLoading(false)
  }


  const handleClearLoggedOutCart = async () => {
    setIsLoading(true)
    removeLocalStorageItem("loggedOutCart")
    removeLocalStorageItem("loggedOutSetupOptions")
    queryClient.invalidateQueries(["loggedOutCartTotal"])
    setIsLoading(false)
    setLoggedOutCartCtx(null)
  }

  const content = (
    <>
      <Row>
        <Col lg="8">

          {/* Logged-in cart */}
          <CartList>
            {shoppingCartQuery.data && shoppingCartQuery.data.products.map((entry: ICartItem) => (
              <MiniCartProduct
                key={entry.product.productID}
                product={entry.product}
                quantity={entry.quantity}
                type="cart"
                shoppingCart={shoppingCartQuery.data.products}
                showSetupOptions={true}
              />
            ))}
          </CartList>

          {/* Logged-out cart */}
          <CartList>
            {(!isUserLoggedIn && loggedOutCartCtx != null) && loggedOutCartCtx.map((entry: ILoggedOutShoppingList) => (
              <MiniCartProduct
                key={entry.product.productID}
                product={entry.product}
                quantity={entry.quantity}
                type="cart"
                showSetupOptions={true}
              />
            ))}
          </CartList>
        </Col>

        <Col lg="4">
          {messagesQuery.data && <CheckoutArea>
            <div className="box-content">
                <>
                  {(shoppingCartQuery.data && isUserLoggedIn) && <div className="total">
                    {messagesQuery.data.messages["total"]}: {shoppingCartQuery.data.cartTotal}
                  </div>}

                    {shoppingCartQuery.data && shippingCostsQuery.data &&
                      <div className="shipping-costs">
                        {messagesQuery.data.messages["shipping-cost"]}: {shippingCostsQuery.data.shippingCost}
                      </div>
                    }

                  {(loggedOutCartTotalQuery.data && !isUserLoggedIn) && <div className="total">
                    {messagesQuery.data.messages["total"]}: {loggedOutCartTotalQuery.data.cartTotal}
                  </div>}

                  <div className="discounts-message">
                    ({messagesQuery.data.messages["discount-warning"]})
                  </div>
                </>
            </div>

            {isUserLoggedIn &&
              <Button variant={"primary"}
                      width="100%"
                      onClick={handleToCheckout}>
                {messagesQuery.data.messages["go-to-checkout"]}
              </Button>}

            {!isUserLoggedIn &&
              <Button variant={"primary"}
                      width="100%"
                      onClick={handleLogin}>
                {messagesQuery.data.messages["login"]} & {messagesQuery.data.messages["checkout"]}
              </Button>}

            {shoppingCartQuery.data && shoppingCartQuery.data.products.length>1 && <div className="clear-cart">
              {!isLoading && <Button onClick={handleClearCart}
                      variant="underline"
                      className="p-0 mt-4 text-muted"
              >
                {messagesQuery.data.messages["clear-cart"]}
              </Button>}

              {isLoading && <Button onClick={handleClearCart}
                                    variant="transparent"
                                    className="p-0 mt-4 text-muted"
              >
                <Spinner animation="border" size="sm"/>
              </Button>}
            </div>}

            {!isLoading && !isUserLoggedIn && <Button onClick={handleClearLoggedOutCart}
                                   variant="underline"
                                   className="p-0 mt-4 text-muted"
            >
              {messagesQuery.data.messages["clear-cart"]}
            </Button>}

          </CheckoutArea>}
        </Col>
      </Row>

      <SecondarySection>
        {/*Other sections...*/}
      </SecondarySection>
    </>
  )


  const emptyCart = (
    <EmptyCart>
      <Image src="/icons/empty-cart.svg" width={120} height={200} alt="Empty Cart"/>
      {messagesQuery.data && <span>{messagesQuery.data.messages["cart-is-empty"]}</span>}
    </EmptyCart>
  )


  return (
    <div className="appContainer">
      <PageHead 
        currentPageTitle={messagesQuery.data?.messages?.["cart"] || ''}
        canonical={typeof window !== 'undefined' ? window.location.href : ''}
        openGraph={{
          title: messagesQuery.data?.messages?.["cart"] || '',
        }}
      />
      
      {messagesQuery.data && <TitleArea title={messagesQuery.data.messages["cart"]} />}

      {hasItems && content}
      {!hasItems && emptyCart}

      {/* Recently Viewed Products Section */}
      <RecentlyViewedProducts className={"mb-4"}/>
    </div>
  );
}

export const getServerSideProps = withServerSideProps(async (context: any) => {
  return {
    props: {}
  }
})


export default Cart
